import { useCallback, useEffect, useRef, useState } from "react";
import { AuthorizationError, OperationError } from "@/shared/errors/auth-errors";
import {
    useFrontendErrorsResource,
    type FrontendErrorsError,
    type FrontendErrorsResource,
    type FrontendErrorsStatus,
} from "@/features/observability/hooks/use-frontend-errors";
import { platformErrorsApi, PlatformErrorsContractError } from "@/features/observability/platform-errors/api/platform-errors.api";
import type {
    PlatformErrorEvent,
    PlatformErrorsEventsParams,
    PlatformErrorsGroups,
    PlatformErrorsPopulationParams,
    PlatformErrorsQueryParams,
    PlatformErrorsSeries,
    PlatformErrorsSummary,
} from "@/features/observability/platform-errors/types/platform-errors.types";

export const usePlatformErrorsSummary = (params: PlatformErrorsQueryParams | null): FrontendErrorsResource<PlatformErrorsSummary> =>
    useFrontendErrorsResource(params, (query, signal) => platformErrorsApi.summary(query, signal));

export const usePlatformErrorsSeries = (params: PlatformErrorsPopulationParams | null): FrontendErrorsResource<PlatformErrorsSeries> =>
    useFrontendErrorsResource(params, (query, signal) => platformErrorsApi.series(query, signal));

export const usePlatformErrorsGroups = (params: PlatformErrorsPopulationParams | null): FrontendErrorsResource<PlatformErrorsGroups> =>
    useFrontendErrorsResource(params, (query, signal) => platformErrorsApi.groups(query, signal));

export type PlatformErrorsEventsResource = {
    status: FrontendErrorsStatus;
    events: PlatformErrorEvent[] | null;
    error: FrontendErrorsError | null;
    hasMore: boolean;
    loadingMore: boolean;
    // No fresh ingestion heartbeat for some page shown: an empty list is not evidence of no events.
    stale: boolean;
    loadMore: () => void;
    refresh: () => void;
};

type EventsSnapshot = {
    key: string | null;
    status: FrontendErrorsStatus;
    events: PlatformErrorEvent[] | null;
    nextCursor: string | null;
    error: FrontendErrorsError | null;
    loadingMore: boolean;
    stale: boolean;
};

const EMPTY: EventsSnapshot = { key: null, status: "idle", events: null, nextCursor: null, error: null, loadingMore: false, stale: false };

const toError = (error: unknown): FrontendErrorsError => {
    if (error instanceof PlatformErrorsContractError) return { code: "contract", message: error.message };
    if (error instanceof AuthorizationError) return { code: "forbidden", message: error.message };
    if (error instanceof OperationError && error.message === "HTTP 503") return { code: "unavailable", message: error.message };
    if (error instanceof Error) return { code: "request", message: error.message };
    return { code: "request", message: "The platform errors source could not be reached" };
};

const STATUS_BY_ERROR: Record<FrontendErrorsError["code"], FrontendErrorsStatus> = {
    contract: "error",
    forbidden: "forbidden",
    unavailable: "unavailable",
    request: "error",
};

const statusOf = (sourceStatus: PlatformErrorsSummary["sourceStatus"]): FrontendErrorsStatus =>
    sourceStatus === "unknown" ? "unavailable" : sourceStatus;

const appendUnseen = (current: PlatformErrorEvent[], page: PlatformErrorEvent[]): PlatformErrorEvent[] => {
    const seen = new Set(current.map((event) => event.receiptId));
    return current.concat(page.filter((event) => {
        if (seen.has(event.receiptId)) return false;
        seen.add(event.receiptId);
        return true;
    }));
};

// Same machine as the frontend-errors drawer: every page after the first is asked with the first page's
// params (the cursor is bound to them, asOf included); unmount or new params cancel whatever is in flight.
export const usePlatformErrorsEvents = (params: PlatformErrorsEventsParams | null): PlatformErrorsEventsResource => {
    const key = params === null ? null : JSON.stringify(params);
    const paramsRef = useRef(params);
    paramsRef.current = params;
    const queryRef = useRef<{ key: string; params: PlatformErrorsEventsParams } | null>(null);
    const seqRef = useRef(0);
    const controllerRef = useRef<AbortController | null>(null);
    const [snapshot, setSnapshot] = useState<EventsSnapshot>(EMPTY);
    const snapshotRef = useRef(snapshot);
    snapshotRef.current = snapshot;

    const begin = useCallback((): { seq: number; controller: AbortController } => {
        seqRef.current += 1;
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        return { seq: seqRef.current, controller };
    }, []);

    const loadFirst = useCallback(async (targetKey: string, targetParams: PlatformErrorsEventsParams): Promise<void> => {
        queryRef.current = { key: targetKey, params: targetParams };
        const { seq, controller } = begin();
        setSnapshot({ ...EMPTY, key: targetKey, status: "loading" });
        try {
            const page = await platformErrorsApi.events({ ...targetParams, cursor: null }, controller.signal);
            if (seq !== seqRef.current) return;
            setSnapshot({
                ...EMPTY,
                key: targetKey,
                status: statusOf(page.sourceStatus),
                stale: page.stale,
                events: page.events === null ? null : appendUnseen([], page.events),
                nextCursor: page.events === null ? null : page.nextCursor,
            });
        } catch (error) {
            if (seq !== seqRef.current || controller.signal.aborted) return;
            const failure = toError(error);
            setSnapshot({ ...EMPTY, key: targetKey, status: STATUS_BY_ERROR[failure.code], error: failure });
        }
    }, [begin]);

    useEffect(() => {
        const current = paramsRef.current;
        if (key === null || current === null) {
            queryRef.current = null;
            return undefined;
        }
        void loadFirst(key, current);
        return () => {
            seqRef.current += 1;
            controllerRef.current?.abort();
        };
    }, [key, loadFirst]);

    const loadMore = useCallback((): void => {
        const query = queryRef.current;
        const current = snapshotRef.current;
        if (query === null || current.key !== query.key || current.loadingMore || current.events === null || current.nextCursor === null) return;
        const cursor = current.nextCursor;
        const { seq, controller } = begin();
        setSnapshot((previous) => ({ ...previous, loadingMore: true, error: null }));
        platformErrorsApi.events({ ...query.params, cursor }, controller.signal).then(
            (page) => {
                if (seq !== seqRef.current) return;
                const pageEvents = page.events;
                if (pageEvents === null) {
                    // The source went away mid-pagination: the pages already shown stay valid for their asOf.
                    setSnapshot((previous) => ({ ...previous, loadingMore: false, error: { code: "unavailable", message: "Platform errors source unavailable" } }));
                    return;
                }
                setSnapshot((previous) => ({
                    ...previous,
                    loadingMore: false,
                    status: previous.status === "degraded" || page.sourceStatus === "degraded" ? "degraded" : previous.status,
                    stale: previous.stale || page.stale,
                    events: appendUnseen(previous.events === null ? [] : previous.events, pageEvents),
                    nextCursor: page.nextCursor,
                }));
            },
            (error: unknown) => {
                if (seq !== seqRef.current || controller.signal.aborted) return;
                const failure = toError(error);
                if (failure.code === "forbidden") {
                    setSnapshot((previous) => ({ ...EMPTY, key: previous.key, status: "forbidden", error: failure }));
                    return;
                }
                setSnapshot((previous) => ({ ...previous, loadingMore: false, error: failure }));
            },
        );
    }, [begin]);

    const refresh = useCallback((): void => {
        const current = paramsRef.current;
        if (key === null || current === null) return;
        void loadFirst(key, current);
    }, [key, loadFirst]);

    if (key === null) return { status: "idle", events: null, error: null, hasMore: false, loadingMore: false, stale: false, loadMore, refresh };
    if (snapshot.key !== key) return { status: "loading", events: null, error: null, hasMore: false, loadingMore: false, stale: false, loadMore, refresh };
    return {
        status: snapshot.status,
        events: snapshot.events,
        error: snapshot.error,
        hasMore: snapshot.nextCursor !== null,
        loadingMore: snapshot.loadingMore,
        stale: snapshot.stale,
        loadMore,
        refresh,
    };
};
